import React, { useState, useContext, useEffect } from "react";

import { MetaMaskContext } from '../context/mm-context'
import Button from "./shared/Button";

function PubSaleDapp(props) {

    // extract the contract data from the props
    const { 
        pubSaleCost,
        pubSaleCostEth, 
        name, 
        remainingPublicSupply, 
        totalSupply, 
        maxMintAmountPerTx,
        maxPubSalePerWallet,
        mintedInPublicSale,
    } = props.data;

    // retrieve site context
    const { ethContract, ethAddress, network } = useContext(MetaMaskContext);

    // set some state
    const [feedback, setFeedback] = useState(<>Please note that you can only mint a maximum of {maxMintAmountPerTx} NFTs per transaction and a maximum of {maxPubSalePerWallet} NFTs per wallet address.</>);
    const [mintAmount, setMintAmount] = useState(1);
    const [claimNft, setClaimNft] = useState(false);

    // mint an NFT
    const mintToken = async () => {

        // preset the feedback
        setFeedback(<>DO NOT REFRESH THE PAGE<br />Please wait while we perform some checks and process the transaction</>);
        
        // set the claiming state
        setClaimNft(true);

        // set the cost and and gas required for the tx
        let cost = pubSaleCost;
        let gasLimit = 285000;
        let totalCostWei = String(cost * mintAmount);
        let totalGasLimit = String(gasLimit * mintAmount);

        try {

            const isSaleActive = await ethContract.isSaleActive()
            if (!isSaleActive) {
                props.getContractData(false)
                throw { code: 99999, message: `Sale is not active` }
            };

            // ensure the public supply isn't zero
            const remaining = await ethContract.getRemainingPublicSupply()
            if (parseInt(remaining) <= 0) throw { code: 99999, message: `All NFTs have been sold` };

            // make sure they aren't exceeding the max amount per tx
            const maxMintAmountPerTx = await ethContract.maxMintAmountPerTx()
            if (mintAmount > parseInt(maxMintAmountPerTx)) throw { code: 99999, message: `Only ${maxMintAmountPerTx} NFTs allowed per transaction` };

            // make sure they aren't exceeding the max amount per wallet
            const minters = await ethContract.minters(ethAddress);
            if (mintAmount + parseInt(minters.hasMintedInPublicSale) > parseInt(maxPubSalePerWallet)) throw { code: 99999, message: `Only ${maxPubSalePerWallet} NFTs allowed per wallet address in the public sale` };

            // attempt to mint
            const tx = await ethContract.mintPubSale(mintAmount, { gasLimit: String(totalGasLimit), from: ethAddress, value: totalCostWei });
            const rc = await tx.wait();
            
            // refresh the contract data without loading spinner
            props.getContractData(false)

            // set minted feedback
            setFeedback(<>You have minted your very own {name} NFT on the <a target="_blank" rel="noreferrer" href={network.etherScan + "tx/" + rc.transactionHash}>Ethereum Blockchain</a>. Head over to <a target="_blank" rel="noreferrer" href={network.openSea + ethAddress}>OpenSea</a> to check it out</>)

            // set claiming back to false
            setClaimNft(false);

            // console.log(rc)

        } catch (err) {
            
            // console.log(err)

            // handle the errors
            let message = '';
            if (err.code === 99999) {
                message += err.message;
            } else if (err.code === 4001) {
                message += 'Transaction cancelled';
            } else {
                message += `Something went wrong.`;
            }
            setFeedback(
                <>
                    <span className="text-warning fw-bold">{message}</span>
                    {err.code !== 99999 && err.code !== 4001 && 
                    <>
                        <br />
                        <span className="text-warning">
                            Please check
                            {" "}
                            <u><a target="_blank" rel="noreferrer" href={network.etherScan + "address/" + ethAddress}>etherscan.io</a></u>
                            {" "}
                            before attempting to mint again to ensure you do not have a pending transaction.
                        </span>
                    </>}
                </>
            );
            setClaimNft(false);
        }
    }

    // lower the amount to mint 
    const decrementMintAmount = (e) => {
        e.preventDefault();
        let newMintAmount = mintAmount - 1;
        if (newMintAmount < 1) {
            newMintAmount = 1;
        }
        setMintAmount(newMintAmount);
    };

    // increase the amount to mint
    const incrementMintAmount = (e) => {
        e.preventDefault();
        let newMintAmount = mintAmount + 1;
        if (newMintAmount > maxMintAmountPerTx) {
            newMintAmount = maxMintAmountPerTx;
        }
        if (newMintAmount > remainingPublicSupply) {
            newMintAmount = remainingPublicSupply;
        }
        setMintAmount(newMintAmount);
    };

    return (
        <>
            <div className="text-tota bg-tota-blur mb-2 rounded mx-auto p-3 noselect" style={{ maxWidth: "350px" }}>

                <div className="mx-auto display-4 fw-bold smallcaps">Public Mint</div>
                
                <div className="mx-auto fs-2 fw-bold smallcaps">{totalSupply <= 2222 ? <>Phase 1</> : totalSupply <= 4444 ? <>Phase 2</> : totalSupply <= 6666 ? <>Phase 3</> : <>Phase 4</>}</div>
                
                <div className="w-100 mt-2 display-0 fw-bold monospace">{remainingPublicSupply}</div>

                <div className="w-100 fs-6 smallcaps small mb-4" style={{ marginTop: "-10px" }}>( remaining )</div>

                <div className="d-flex justify-content-center align-items-center mb-3">
                    <div className="input-group mb-1" style={{ width: "175px" }}>
                        <span className="input-group-text fs-4 cursor-pointer bg-tota-dark border-tota-dark text-tota change-amount" onClick={(e) => { decrementMintAmount(e) }}>
                            <i className="bi bi-dash" style={{ marginBottom: "-4px" }}></i>
                        </span>
                        <input type="text" className="form-control text-center p-0 noselect border-0 bg-none input-disabled-tota" style={{ fontSize: "40px" }} disabled value={mintAmount} />
                        <span className="input-group-text fs-4 cursor-pointer bg-tota-dark border-tota-dark text-tota change-amount" onClick={(e) => { incrementMintAmount(e) }}>
                            <i className="bi bi-plus" style={{ marginBottom: "-4px" }}></i>
                        </span>
                    </div>
                </div>

                {pubSaleCostEth && <div className="fs-6 fw-bold mx-auto my-4" style={{ maxWidth: "300px", letterSpacing: "1px" }}>Each NFT costs {pubSaleCostEth} ETH <br /> <span className="small smallcaps">( plus gas )</span></div>}

                <Button
                    content={!claimNft && "MINT" || "MINTING..."}
                    colour={!claimNft && "btn-tota-dark" || "btn-tota-dark"}
                    onClick={mintToken}
                    disabled={claimNft && 'disabled'}
                    style={{ width: "250px" }}
                    size={"btn-lg"}
                    styles={"mx-auto " + (claimNft ? "cursor-wait" : "")}
                />
            </div>

            {feedback &&
                <div className={`text-tota bg-tota-blur mt-3 mx-auto p-2`} role="alert" style={{ maxWidth: "350px" }}>
                    {feedback}
                </div>
            }

            <div className="text-tota bg-tota-blur small rounded text-center mt-5 mb-2 p-2 mx-auto" role="alert" style={{ maxWidth: "500px" }}>
                <p className="fw-bold">Please Read</p>
                <p>Once you make the purchase, it can't be reversed or cancelled</p>
                <p>We have set the gas limit to 285000 GWEI for the contract to successfully mint your NFT. We recommend that you don't lower the gas limit</p>
            </div>
        </>
    );
}

export default PubSaleDapp;
